import GenericLoading from '@cannect/components/GenericLoading'
import { Alert } from 'antd'
import { useEffect, useState } from 'react'
import { LuTrash2 } from 'react-icons/lu'
import { toast } from 'react-toastify'
import { useOrderCart } from '../hooks/useOrderCart'
import {
  AddProductsButton,
  CartContainer,
  CartHeader,
  CartItemCard,
  CartItemImage,
  CartItemInfo,
  CartItemName,
  CartItemPrice,
  CartItemQuantity,
  CartItemsContainer,
  CartSummary,
  CartTitle,
  CheckoutButton,
  CouponButton,
  CouponContainer,
  CouponInput,
  EmptyCart,
  QuantityButton,
  QuantityValue,
  RemoveButton,
  SummaryContainer,
  SummaryLine,
  SummaryLines,
  TotalLabel,
  TotalValue
} from '../styles'

interface CartListProps {
  onNavigateToProducts: () => void
  onNavigateToCheckout: () => void
}

export default function CartList({ onNavigateToProducts, onNavigateToCheckout }: CartListProps) {
  const { order, loading, fetchOrder, updateOrderItems, applyCoupon } = useOrderCart()
  const [couponInput, setCouponInput] = useState('')
  const [couponLoading, setCouponLoading] = useState(false)

  useEffect(() => {
    fetchOrder()
  }, [fetchOrder])

  const handleUpdateQuantity = async (itemId: number, newQuantity: number) => {
    if (!order || newQuantity < 1) return

    const updatedItems = order.items.map((item) => (item.id === itemId ? { ...item, quantity: newQuantity } : item))

    try {
      await updateOrderItems(updatedItems)
      toast.success('Quantidade atualizada')
    } catch (err) {
      toast.error('Erro ao atualizar quantidade')
    }
  }

  const handleRemove = async (itemId: number) => {
    if (!order) return

    const updatedItems = order.items.filter((item) => item.id !== itemId)

    try {
      await updateOrderItems(updatedItems)
      toast.success('Produto removido do carrinho')
    } catch (err) {
      toast.error('Erro ao remover produto')
    }
  }

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) {
      toast.error('Digite um cupom válido')
      return
    }

    try {
      setCouponLoading(true)
      await applyCoupon(couponInput)
      toast.success('Cupom validado com sucesso')
      setCouponInput('')
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Cupom inválido'
      toast.error(message)
    } finally {
      setCouponLoading(false)
    }
  }

  if (loading) {
    return <GenericLoading loading />
  }

  if (!order || order.items.length === 0) {
    return (
      <EmptyCart>
        <p>Seu carrinho está vazio</p>
        <AddProductsButton onClick={onNavigateToProducts}>Adicionar Produtos</AddProductsButton>
      </EmptyCart>
    )
  }

  const subtotal = parseFloat(order.subtotal || '0')
  const discount = Number(order.discount || 0)
  const total = parseFloat(order.amount || '0')
  const formattedCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)

  return (
    <CartContainer>
      <CartHeader>
        <CartTitle>Meu Carrinho</CartTitle>
        <AddProductsButton onClick={onNavigateToProducts}>Adicionar Mais Produtos</AddProductsButton>
      </CartHeader>

      <Alert
        message="Atenção"
        description="A dispensação do produto está condicionada a aprovação dos documentos."
        type="info"
        showIcon
        style={{ marginBottom: '16px' }}
      />

      <CartItemsContainer>
        <CartItemCard>
          <CartItemName>Produto</CartItemName>
          <CartItemName>Quantidade</CartItemName>
          <CartItemName>Valor Unitário</CartItemName>
          <CartItemName>Valor Total</CartItemName>
        </CartItemCard>
        {order.items.map((item) => {
          const image = item.product.media.find((m) => m.format === 'IMG')?.path || ''
          const unitPrice = parseFloat(item.unitPrice)
          const totalPrice = unitPrice * item.quantity
          const isProduct = item.type === 'PRODUTO'

          return (
            <CartItemCard key={item.id}>
              <CartItemInfo>
                <CartItemName>
                  <CartItemImage src={image} alt={item.product.translated_name} />
                  <span>{item.product.translated_name}</span>
                </CartItemName>
                <CartItemQuantity>
                  {isProduct ? (
                    <>
                      <QuantityButton onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}>
                        -
                      </QuantityButton>
                      <QuantityValue>{item.quantity}</QuantityValue>
                      <QuantityButton onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}>
                        +
                      </QuantityButton>
                      <RemoveButton onClick={() => handleRemove(item.id)} title="Remover produto">
                        <LuTrash2 />
                      </RemoveButton>
                    </>
                  ) : (
                    <QuantityValue>{item.quantity}</QuantityValue>
                  )}
                </CartItemQuantity>
                <CartItemPrice>
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  }).format(unitPrice)}
                </CartItemPrice>
                <CartItemPrice>
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  }).format(totalPrice)}
                </CartItemPrice>
              </CartItemInfo>
            </CartItemCard>
          )
        })}
      </CartItemsContainer>

      <CartSummary>
        <CouponContainer>
          <CouponInput
            type="text"
            placeholder={order.coupon?.name ? `Cupom aplicado: ${order.coupon.name}` : 'Digite seu cupom'}
            value={couponInput}
            onChange={(e) => setCouponInput(e.target.value)}
            disabled={couponLoading}
          />
          <CouponButton onClick={handleApplyCoupon} disabled={couponLoading || !couponInput.trim()}>
            {couponLoading ? 'Validando...' : 'Validar'}
          </CouponButton>
        </CouponContainer>

        <SummaryLines>
          <SummaryLine>
            <TotalLabel>Subtotal:</TotalLabel>
            <TotalValue>{formattedCurrency(subtotal)}</TotalValue>
          </SummaryLine>
          <SummaryLine>
            <TotalLabel>Desconto:</TotalLabel>
            <TotalValue>{discount > 0 ? `- ${formattedCurrency(discount)}` : formattedCurrency(0)}</TotalValue>
          </SummaryLine>
          <SummaryContainer>
            <TotalLabel>Total:</TotalLabel>
            <TotalValue>{formattedCurrency(total)}</TotalValue>
          </SummaryContainer>
        </SummaryLines>

        <CheckoutButton onClick={onNavigateToCheckout}>Finalizar Compra</CheckoutButton>
      </CartSummary>
    </CartContainer>
  )
}
